import { collection, getDocs, getDoc, addDoc, doc, setDoc, onSnapshot, Unsubscribe, updateDoc, deleteDoc, query, where, writeBatch, WhereFilterOp } from 'firebase/firestore';
import { db } from '@/firebase';
import { Trip } from '@/types';

const tripsCollectionRef = collection(db, 'trips');

export type TripFilter = [string, WhereFilterOp, unknown];

/**
 * Richtet einen Echtzeit-Listener für die gesamte Trips-Sammlung ein.
 * @param callback Die Funktion, die bei jeder Datenaktualisierung mit der neuen Liste von Reisen aufgerufen wird.
 * @param filters Ein optionales Array von Filtern, die auf die Abfrage angewendet werden sollen.
 * @returns Eine `unsubscribe`-Funktion, um den Listener zu entfernen.
 */
export const listenToTrips = (callback: (trips: Trip[]) => void, filters: TripFilter[] = []): Unsubscribe => {
    const q = filters.length > 0
        ? query(tripsCollectionRef, ...filters.map(f => where(...f)))
        : query(tripsCollectionRef);

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const trips = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Trip));
        callback(trips);
    });
    return unsubscribe;
};

/**
 * Ruft alle Reisen einmalig aus Firestore ab.
 * @returns Ein Promise, das zu einem Array von Trip-Objekten aufgelöst wird.
 */
export const getTrips = async (): Promise<Trip[]> => {
    const snapshot = await getDocs(tripsCollectionRef);
    const trips = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Trip));
    return trips;
};


/**
 * Richtet einen Echtzeit-Listener für ein einzelnes Trip-Dokument ein.
 * @param tripId Die ID der zu beobachtenden Reise.
 * @param callback Die Funktion, die bei jeder Datenaktualisierung mit dem neuen Trip-Objekt aufgerufen wird.
 * @returns Eine `unsubscribe`-Funktion, um den Listener zu entfernen.
 */
export const listenToTripById = (tripId: string, callback: (trip: Trip | undefined) => void): Unsubscribe => {
    const tripDoc = doc(db, 'trips', tripId);
    const unsubscribe = onSnapshot(tripDoc, (docSnap) => {
        if (docSnap.exists()) {
            callback({ id: docSnap.id, ...docSnap.data() } as Trip);
        } else {
            callback(undefined);
        }
    });
    return unsubscribe;
};

/**
 * Ruft eine einzelne Reise anhand ihrer ID aus Firestore ab (einmaliger Abruf).
 * @param tripId Die ID der abzurufenden Reise.
 * @returns Das Reiseobjekt oder undefined, wenn es nicht gefunden wurde.
 */
export const getTripById = async (tripId: string): Promise<Trip | undefined> => {
    const tripDoc = doc(db, 'trips', tripId);
    const tripSnap = await getDoc(tripDoc);
    if (tripSnap.exists()) {
        return { id: tripSnap.id, ...tripSnap.data() } as Trip;
    }
    return undefined;
};

/**
 * Fügt eine neue Reise zu Firestore hinzu.
 * @param newTripData Die hinzuzufügenden Reisedaten (ohne ID).
 * @returns Die ID der neu erstellten Reise.
 */
export const addTrip = async (newTripData: Omit<Trip, 'id'>): Promise<string> => {
    const docRef = await addDoc(tripsCollectionRef, newTripData);
    return docRef.id;
};

/**
 * Aktualisiert eine bestehende Reise in Firestore, indem ihre Daten ersetzt werden.
 * @param updatedTrip Das vollständige Reiseobjekt, einschließlich seiner ID.
 */
export const updateTrip = async (updatedTrip: Trip): Promise<void> => {
    const tripDoc = doc(db, 'trips', updatedTrip.id);
    await updateDoc(tripDoc, { ...updatedTrip });
};

export const deleteTrip = async (tripId: string): Promise<void> => {
    const tripDoc = doc(db, 'trips', tripId);
    await deleteDoc(tripDoc);
};

// --- Subcollection Getters ---

export const getSubcollection = async <T>(tripId: string, collectionName: string): Promise<T[]> => {
    const q = query(collection(db, `trips/${tripId}/${collectionName}`));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as T));
};

// --- Subcollection Batch Writers ---

export const addItemsToSubcollection = async <T extends { id?: string }>(tripId: string, collectionName: string, items: T[]) => {
    const batch = writeBatch(db);
    const subcollectionRef = collection(db, `trips/${tripId}/${collectionName}`);
    items.forEach(item => {
        const { id, ...data } = item; // remove id from data
        const docRef = doc(subcollectionRef); // create new doc with new id
        batch.set(docRef, data);
    });
    await batch.commit();
};

// Die Funktionen `saveTripCollectionRepo`, `importCollection` und `getTripCollection`
// aus dem alten Repository sind jetzt veraltet.
// - `getTripCollection` wird durch `listenToTrips` oder `getTrips` ersetzt.
// - `saveTripCollectionRepo` und `importCollection` arbeiteten an der gesamten Sammlung auf einmal,
//   was nicht der typischen Interaktion mit Firestore entspricht. Daten werden jetzt
//   pro Dokument mit `addTrip` und `updateTrip` bearbeitet.
//   Wenn eine Funktion für den Massenimport benötigt wird, kann eine dedizierte Funktion dafür erstellt werden.
